import { supabase } from '../lib/supabase';

/**
 * User Management Service
 * Handles all user-related database operations for the People page
 */

/**
 * Fetch all users with their profile information
 * @returns {Promise<Array>} Array of user objects with profile data
 */
export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select(`
        id,
        email,
        full_name,
        role,
        organization,
        team_id,
        created_at,
        updated_at
      `)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('getAllUsers service error:', error);
    throw error;
  }
};

/**
 * Fetch user authorizations from role_email_authorizations table
 * @returns {Promise<Array>} Array of authorization objects
 */
export const getUserAuthorizations = async () => {
  try {
    const { data, error } = await supabase?.from('role_email_authorizations')?.select(`
        id,
        email,
        role,
        team_number,
        team_role,
        is_active,
        created_at,
        updated_at
      `)?.eq('is_active', true)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user authorizations:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('getUserAuthorizations service error:', error);
    throw error;
  }
};

/**
 * Add new user authorization
 * @param {Object} userData - User data to insert
 * @param {string} userData.email - User email
 * @param {string} userData.fullName - User full name
 * @param {string} userData.role - User role (student, city_official, pcf_admin)
 * @param {number} [userData.teamNumber] - Team number (only for students)
 * @param {string} [userData.teamRole] - Team role (only for students)
 * @returns {Promise<Object>} Inserted user authorization object
 */
export const addUserAuthorization = async (userData) => {
  try {
    const { email, fullName, role, teamNumber, teamRole } = userData;

    // Insert into role_email_authorizations
    const { data, error } = await supabase?.from('role_email_authorizations')?.insert([
        {
          email: email?.toLowerCase(),
          full_name: fullName,
          role,
          team_number: teamNumber || null,
          team_role: teamRole || null,
          is_active: true
        }
      ])?.select()?.single();

    if (error) throw error;

    // NEW: If teamNumber is provided, increment current_members count
    if (teamNumber) {
      // Fetch current team data
      const { data: teamData, error: fetchError } = await supabase
        ?.from('teams')
        ?.select('current_members')
        ?.eq('team_number', teamNumber)
        ?.single();

      if (!fetchError && teamData) {
        // Increment the count
        const { error: updateError } = await supabase
          ?.from('teams')
          ?.update({ current_members: (teamData?.current_members || 0) + 1 })
          ?.eq('team_number', teamNumber);

        if (updateError) {
          console.error('Error incrementing team member count:', updateError);
          // Don't throw error here - authorization was successful
        }
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error adding user authorization:', error);
    return { data: null, error };
  }
};

/**
 * Update user authorization
 * @param {string} id - Authorization ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated authorization object
 */
export const updateUserAuthorization = async (id, updates) => {
  try {
    if (!id) {
      throw new Error('Authorization ID is required');
    }

    const { data, error } = await supabase?.from('role_email_authorizations')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) {
      console.error('Error updating user authorization:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('updateUserAuthorization service error:', error);
    throw error;
  }
};

/**
 * Deactivate user authorization (soft delete)
 * @param {string} id - Authorization ID
 * @returns {Promise<Object>} Updated authorization object
 */
export const deactivateUserAuthorization = async (id) => {
  try {
    if (!id) {
      throw new Error('Authorization ID is required');
    }

    const { data, error } = await supabase?.from('role_email_authorizations')?.update({
        is_active: false,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) {
      console.error('Error deactivating user authorization:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('deactivateUserAuthorization service error:', error);
    throw error;
  }
};

/**
 * Check if email is already authorized
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} True if email exists and is active
 */
export const isEmailAuthorized = async (email) => {
  try {
    if (!email) {
      return false;
    }

    const { data, error } = await supabase?.from('role_email_authorizations')?.select('id')?.eq('email', email?.toLowerCase()?.trim())?.eq('is_active', true)?.limit(1);

    if (error) {
      console.error('Error checking email authorization:', error);
      return false;
    }

    return data && data?.length > 0;
  } catch (error) {
    console.error('isEmailAuthorized service error:', error);
    return false;
  }
};

/**
 * Send authorization email to newly added user
 * @param {Object} userData - User data
 * @param {string} userData.email - User email
 * @param {string} userData.fullName - User full name
 * @param {string} userData.role - User role
 * @returns {Promise<Object>} Response from email service
 */
export const sendAuthorizationEmail = async (userData) => {
  try {
    const { email, fullName, role } = userData;
    
    // Get the app URL from environment or use default
    const appUrl = import.meta.env?.VITE_APP_URL || window.location?.origin;
    const signupUrl = `${appUrl}/user-registration`;

    // Call the send-email edge function
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        to: email,
        subject: 'You have been authorized to join Smart Cities Hub',
        type: 'user_authorization',
        data: {
          fullName,
          role,
          signupUrl
        }
      }
    });

    // Handle edge function errors
    if (error) {
      let errorMessage = 'Failed to send authorization email';
      
      // The edge function returns error in the response body as JSON
      // Try to extract it from various possible locations
      try {
        // First, try to parse the response body if it exists
        if (error?.context?.body) {
          // If body is already an object
          if (typeof error?.context?.body === 'object' && error?.context?.body?.error) {
            errorMessage = error?.context?.body?.error;
          }
          // If body is a string, try to parse it
          else if (typeof error?.context?.body === 'string') {
            try {
              const parsed = JSON.parse(error?.context?.body);
              if (parsed?.error) {
                errorMessage = parsed?.error;
              }
            } catch (e) {
              // If parsing fails, use the raw string
              errorMessage = error?.context?.body;
            }
          }
        }
        // Fallback to error message if it's not the generic one
        else if (error?.message && error?.message !== 'Edge Function returned a non-2xx status code') {
          errorMessage = error?.message;
        }
      } catch (e) {
        console.error('Error parsing edge function error:', e);
      }
      
      console.error('Edge function error details:', {
        error,
        extractedMessage: errorMessage
      });
      
      throw new Error(errorMessage);
    }

    // Check if the response data indicates failure
    if (data && !data?.success) {
      let errorMessage = data?.error || 'Failed to send authorization email';
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error sending authorization email:', error);
    return { success: false, error: error?.message || 'An unexpected error occurred' };
  }
};

/**
 * Send custom email to user
 * @param {Object} emailData - Email data
 * @param {string} emailData.to - Recipient email
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.message - Email message
 * @returns {Promise<Object>} Response from email service
 */
export const sendCustomEmail = async (emailData) => {
  try {
    const { to, subject, message } = emailData;

    // Call the send-email edge function
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        to,
        subject,
        type: 'custom',
        data: {
          message
        }
      }
    });

    // Check if the response data indicates failure (edge function returned 500 with error in body)
    if (data && data?.success === false) {
      let errorMessage = data?.error || 'Failed to send email';
      console.error('Edge function returned error:', errorMessage);
      throw new Error(errorMessage);
    }

    // Handle edge function errors (network/timeout errors)
    if (error) {
      let errorMessage = 'Failed to send email';

      // The edge function returns error in the response body as JSON
      // Try to extract it from various possible locations
      try {
        // First, try to parse the response body if it exists
        if (error?.context?.body) {
          // If body is already an object
          if (typeof error?.context?.body === 'object' && error?.context?.body?.error) {
            errorMessage = error?.context?.body?.error;
          }
          // If body is a string, try to parse it
          else if (typeof error?.context?.body === 'string') {
            try {
              const parsed = JSON.parse(error?.context?.body);
              if (parsed?.error) {
                errorMessage = parsed?.error;
              }
            } catch (e) {
              // If parsing fails, use the raw string
              errorMessage = error?.context?.body;
            }
          }
        }
        // Fallback to error message if it's not the generic one
        else if (error?.message && error?.message !== 'Edge Function returned a non-2xx status code') {
          errorMessage = error?.message;
        }
      } catch (e) {
        console.error('Error parsing edge function error:', e);
      }

      console.error('Edge function error:', errorMessage);
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error sending custom email:', error);
    return { success: false, error: error?.message || 'An unexpected error occurred' };
  }
};

/**
 * Delete user authorization and associated data
 * @param {string} id - Authorization ID
 * @param {string} email - User email
 * @returns {Promise<Object>} Deletion result
 */
export const deleteUserAuthorization = async (id, email) => {
  try {
    if (!id) {
      throw new Error('Authorization ID is required');
    }

    // First, check if user has an active profile
    const { data: userProfile } = await supabase?.from('user_profiles')?.select('id, team_id')?.eq('email', email?.toLowerCase())?.single();

    // If user has a profile, we need to clean up related data AND revoke auth access
    if (userProfile) {
      const userId = userProfile?.id;
      const teamId = userProfile?.team_id;

      // Remove from team_members if they're in a team
      if (teamId) {
        await supabase?.from('team_members')?.delete()?.eq('user_id', userId);
      }

      // Remove from conversation_participants
      await supabase?.from('conversation_participants')?.delete()?.eq('user_id', userId);

      // Delete user profile
      await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

      // Revoke Supabase authentication access
      const { error: authError } = await supabase?.auth?.admin?.deleteUser(userId);
      
      if (authError) {
        console.error('Error revoking auth access:', authError);
        // Continue even if auth deletion fails (user data already cleaned up)
      }
    }

    // Finally, delete the authorization record
    const { error } = await supabase?.from('role_email_authorizations')?.delete()?.eq('id', id);

    if (error) {
      console.error('Error deleting user authorization:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('deleteUserAuthorization service error:', error);
    throw error;
  }
};